import { alpha, Box, Button, Divider, IconButton, Menu, Typography, useTheme } from '@mui/material'
import { useTranslations } from 'next-intl'
import type { MouseEvent } from 'react'
import { useRef } from 'react'

import type { HotelRoomConfig } from '../searchForm/hooks/useHotelSearchManager'

const ROOMS_LIST_MAX_HEIGHT = 280

interface GuestRoomSelectorProps {
    roomConfigs: HotelRoomConfig[]
    minRooms: number
    maxRooms: number
    minAdultsPerRoom: number
    maxAdultsPerRoom: number
    minChildrenPerRoom: number
    maxChildrenPerRoom: number
    onRoomCountChange: (count: number) => void
    onRoomAdultsChange: (roomIndex: number, adults: number) => void
    onRoomChildrenChange: (roomIndex: number, children: number) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
}

interface CounterRowProps {
    label: string
    hint?: string
    value: number
    canDecrease: boolean
    canIncrease: boolean
    onDecrease: () => void
    onIncrease: () => void
    onMouseDown: (e: MouseEvent) => void
}

const StepperControls: React.FC<
    Pick<CounterRowProps, 'value' | 'canDecrease' | 'canIncrease' | 'onDecrease' | 'onIncrease' | 'onMouseDown'>
> = ({ value, canDecrease, canIncrease, onDecrease, onIncrease, onMouseDown }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <IconButton
            size="small"
            onMouseDown={onMouseDown}
            onClick={e => {
                e.stopPropagation()
                onDecrease()
            }}
            disabled={!canDecrease}
            aria-label="decrease"
            sx={{ minWidth: 32, minHeight: 32, fontSize: '18px', fontWeight: 'bold' }}
        >
            -
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center', mx: 1 }}>
            {value}
        </Typography>
        <IconButton
            size="small"
            onMouseDown={onMouseDown}
            onClick={e => {
                e.stopPropagation()
                onIncrease()
            }}
            disabled={!canIncrease}
            aria-label="increase"
            sx={{ minWidth: 32, minHeight: 32, fontSize: '18px', fontWeight: 'bold' }}
        >
            +
        </IconButton>
    </Box>
)

const CounterRow: React.FC<CounterRowProps> = ({
    label,
    hint,
    value,
    canDecrease,
    canIncrease,
    onDecrease,
    onIncrease,
    onMouseDown
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 1,
            minHeight: 44
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                flex: 1
            }}
        >
            <Typography variant="body2" fontWeight={500} color="text.primary" noWrap>
                {label}
            </Typography>
            {hint && (
                <Typography variant="caption" color="text.secondary" noWrap>
                    {hint}
                </Typography>
            )}
        </Box>
        <StepperControls
            value={value}
            canDecrease={canDecrease}
            canIncrease={canIncrease}
            onDecrease={onDecrease}
            onIncrease={onIncrease}
            onMouseDown={onMouseDown}
        />
    </Box>
)

export const GuestRoomSelector: React.FC<GuestRoomSelectorProps> = ({
    roomConfigs,
    minRooms,
    maxRooms,
    minAdultsPerRoom,
    maxAdultsPerRoom,
    minChildrenPerRoom,
    maxChildrenPerRoom,
    onRoomCountChange,
    onRoomAdultsChange,
    onRoomChildrenChange,
    active,
    onActivate,
    onClose
}) => {
    const anchorRef = useRef(null)
    const t = useTranslations('common')
    const theme = useTheme()

    const totalGuests = roomConfigs.reduce((sum, r) => sum + r.adults + r.children, 0)
    const summaryLabel = `${roomConfigs.length} ${t('room', { s: roomConfigs.length > 1 ? 's' : '' })}, ${totalGuests} ${t('guest', { s: totalGuests !== 1 ? 's' : '' })}`

    const handleToggle = () => {
        if (active) {
            onClose()
        } else {
            onActivate()
        }
    }

    const stopPropagation = (e: MouseEvent) => e.stopPropagation()

    const roomCount = roomConfigs.length

    return (
        <Box ref={anchorRef} sx={{ width: '100%' }}>
            <Button
                variant="text"
                onClick={handleToggle}
                sx={{
                    textTransform: 'none',
                    backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                        backgroundColor: active ? alpha(theme.palette.primary.main, 0.12) : 'action.hover'
                    },
                    width: '100%',
                    justifyContent: 'flex-start'
                }}
            >
                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {summaryLabel}
                </Typography>
            </Button>
            <Menu
                anchorEl={anchorRef.current}
                open={active}
                onClose={onClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ zIndex: 2000 }}
                slotProps={{
                    list: { onMouseDown: stopPropagation, sx: { p: 0 } },
                    paper: {
                        onMouseDown: stopPropagation,
                        sx: {
                            minWidth: 340,
                            maxWidth: 400,
                            mt: 1,
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider',
                            boxShadow: theme.shadows[4],
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.25,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}
                    onMouseDown={stopPropagation}
                >
                    <CounterRow
                        label={t('howManyRooms')}
                        value={roomCount}
                        canDecrease={roomCount > minRooms}
                        canIncrease={roomCount < maxRooms}
                        onDecrease={() => onRoomCountChange(roomCount - 1)}
                        onIncrease={() => onRoomCountChange(roomCount + 1)}
                        onMouseDown={stopPropagation}
                    />
                </Box>

                <Box
                    sx={{
                        maxHeight: ROOMS_LIST_MAX_HEIGHT,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: 3,
                            bgcolor: 'grey.300'
                        }
                    }}
                    onMouseDown={stopPropagation}
                >
                    {roomConfigs.map((room, index) => (
                        <Box key={index}>
                            {index > 0 && <Divider />}
                            <Box sx={{ px: 2, py: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color="primary.main"
                                    sx={{ display: 'block', pt: 1, pb: 0.25, textTransform: 'uppercase', letterSpacing: 0.4 }}
                                >
                                    {t('roomLabel', { number: index + 1 })}
                                </Typography>
                                <CounterRow
                                    label={t('adult')}
                                    hint={t('adultsAgeHint')}
                                    value={room.adults}
                                    canDecrease={room.adults > minAdultsPerRoom}
                                    canIncrease={room.adults < maxAdultsPerRoom}
                                    onDecrease={() => onRoomAdultsChange(index, room.adults - 1)}
                                    onIncrease={() => onRoomAdultsChange(index, room.adults + 1)}
                                    onMouseDown={stopPropagation}
                                />
                                <CounterRow
                                    label={t('child')}
                                    hint={t('childrenAgeHint')}
                                    value={room.children}
                                    canDecrease={room.children > minChildrenPerRoom}
                                    canIncrease={room.children < maxChildrenPerRoom}
                                    onDecrease={() => onRoomChildrenChange(index, room.children - 1)}
                                    onIncrease={() => onRoomChildrenChange(index, room.children + 1)}
                                    onMouseDown={stopPropagation}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Menu>
        </Box>
    )
}
